import { useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { API, QUERY_KEYS } from '@config';

import { useUserState, useRequest, useDisplayResponseMessage } from '@hooks';

import {
  OptionsContext,
  setAppInfo,
  setOrganizations,
  setActiveOrganization,
  setActiveProject,
  setActiveFlow,
} from '@reducers';

export const useOptionsContext = () => {
  const context = useContext(OptionsContext);
  return context;
};

export const useOptions = () => {
  const { state } = useContext(OptionsContext);
  return state;
};

export const useOptionsDispatch = () => {
  const { dispatch } = useContext(OptionsContext);
  return dispatch;
};

export const useFetchAppInfo = (): void => {
  const request = useRequest();
  const dispatch = useOptionsDispatch();
  const displayResponseMessage = useDisplayResponseMessage();

  useQuery({
    queryKey: [QUERY_KEYS.APP_INFO],
    queryFn: async () => {
      try {
        const { data } = await request<AppInfo>(API.CORE_APP_INFO());
        dispatch(setAppInfo(data));
        return data;
      } catch (error) {
        console.error(error);
        displayResponseMessage('app_info.fetch.error');
        return null;
      }
    },
  });
};

export const useAppInfo = () => {
  const { state } = useContext(OptionsContext);
  return state.appInfo;
};

export const useFetchOptions = (): void => {
  const request = useRequest();
  const dispatch = useOptionsDispatch();
  const { loggedIn } = useUserState();
  const displayResponseMessage = useDisplayResponseMessage();

  // Fetch available organizations with user roles. Projects and flows are no
  // longer fetched eagerly — components fetch what they display on demand
  // via paginated queries and single-entity hydration.
  useQuery({
    enabled: loggedIn,
    queryKey: [QUERY_KEYS.ORGANIZATIONS],
    queryFn: async () => {
      try {
        const { data } = await request<OrganizationWithRole[]>(API.CORE_ORGANIZATIONS_PROFILE_LIST());
        dispatch(setOrganizations(data));
        return data;
      } catch (error) {
        console.error(error);
        displayResponseMessage('organizations.fetch.error');
        return null;
      }
    },
  });
};

export const useRefetchOptions = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATIONS] });
  };
};

export const useOrganizations = () => {
  const { state } = useContext(OptionsContext);
  return state.organizations;
};

export const useActiveOrganization = () => {
  const { state } = useContext(OptionsContext);
  return state.activeOrganization;
};

export const useActiveOrganizationUuid = () => {
  const { state } = useContext(OptionsContext);
  return state.activeOrganization?.uuid;
};

export const useSetActiveOrganization = () => {
  const dispatch = useOptionsDispatch();
  return (organizationUuid: string, organization?: OrganizationWithRole | null) => {
    dispatch(setActiveOrganization(organizationUuid, organization));
  };
};

export const useHasWritePermission = () => {
  const activeOrganization = useActiveOrganization();
  const userRole = (activeOrganization as OrganizationWithRole)?.user_role;

  // Viewers have read-only access (viewer role)
  // All other roles (owner, admin, member) have write access
  return activeOrganization && userRole !== 'viewer';
};

export const useHasAdminPermission = () => {
  const activeOrganization = useActiveOrganization();
  // Only owners and admins have admin permissions
  const role = (activeOrganization as OrganizationWithRole)?.user_role;
  return role === 'owner' || role === 'admin';
};

export const useActiveProject = () => {
  const { state } = useContext(OptionsContext);
  return state.activeProject;
};

export const useActiveProjectUuid = () => {
  const { state } = useContext(OptionsContext);
  return state.activeProject?.uuid;
};

export const useSetActiveProject = () => {
  const dispatch = useOptionsDispatch();
  return (projectUuid: string, project?: Project | null) => {
    dispatch(setActiveProject(projectUuid, project));
  };
};

export const useActiveFlow = () => {
  const { state } = useContext(OptionsContext);
  return state.activeFlow;
};

export const useActiveFlowUuid = () => {
  const { state } = useContext(OptionsContext);
  return state.activeFlow === 'all' ? state.activeFlow : state.activeFlow?.uuid;
};

export const useSetActiveFlow = () => {
  const dispatch = useOptionsDispatch();
  return (flowUuid: string, flow?: Flow | null) => {
    dispatch(setActiveFlow(flowUuid, flow));
  };
};

export const useOrganizationsWithRole = () => {
  const request = useRequest();
  const displayResponseMessage = useDisplayResponseMessage();
  const { loggedIn } = useUserState();

  return useQuery({
    enabled: loggedIn,
    queryKey: [QUERY_KEYS.ORGANIZATIONS, 'with-role'],
    queryFn: async () => {
      try {
        const { data } = await request<OrganizationWithRole[]>(API.CORE_ORGANIZATIONS_PROFILE_LIST());
        return data;
      } catch (error) {
        console.error('Failed to fetch organizations with role:', error);
        displayResponseMessage('organizations.fetch.error');
        return [];
      }
    },
  });
};

export const useLeaveOrganization = () => {
  const request = useRequest();
  const refetchOptions = useRefetchOptions();
  const displayResponseMessage = useDisplayResponseMessage();

  return async (organization: Organization) => {
    try {
      await request(API.CORE_ORGANIZATIONS_LEAVE(organization.uuid), {
        method: 'POST',
        data: { organization },
      });
      refetchOptions();
      displayResponseMessage('organizations.leave.success', organization);
    } catch (error) {
      console.error(error);
      displayResponseMessage('organizations.leave.error', organization);
    }
  };
};

